import React, { useEffect, useMemo, useState } from "react";
import ParticipantCheckInQRCard from "./ParticipantCheckInQRCard";
import { IconChevronLeft, IconChevronRight } from "../shared/icons/IconLib";
import {
  buildParticipantQrEventOptions,
  enrichParticipantQrEntriesForEvent,
  formatParticipantQrListLabel,
  formatParticipantQrListStatusLabel,
  participantQrEntryMatchesSearch,
  sortParticipantQrEntriesByRegistered,
} from "../shared/utils/eventCalendarParticipantQr";
import { formatEventFilterOptionLabel } from "../shared/utils/eventFilterOptionLabel";

/**
 * Check-in QR view: simple event select (name — date), one QR card with prev/next,
 * and a right-side list of household members (registered first) with the active
 * member highlighted. Requires a registered event; defaults to the first one.
 */
function ParticipantCheckInQRPanel({
  familyEntries = [],
  registeredEventRowsForOptions = [],
  allRegisteredEventRows = [],
  accountId,
  serverUrl,
  searchLower = "",
}) {
  const eventOptions = useMemo(
    () => buildParticipantQrEventOptions(registeredEventRowsForOptions),
    [registeredEventRowsForOptions]
  );

  const eventSelectOptions = useMemo(
    () =>
      eventOptions.map((opt) => ({
        value: opt.eventId,
        label: formatEventFilterOptionLabel({
          name: opt.eventName,
          start_datetime: opt.start_datetime,
        }),
      })),
    [eventOptions]
  );

  const [selectedEventId, setSelectedEventId] = useState("");
  const [activeUserId, setActiveUserId] = useState("");

  const hasRegisteredEvents = eventOptions.length > 0;

  useEffect(() => {
    setSelectedEventId((prev) => {
      const id = prev != null ? String(prev).trim() : "";
      if (id && eventOptions.some((o) => o.eventId === id)) return id;
      return eventOptions.length > 0 ? eventOptions[0].eventId : "";
    });
  }, [eventOptions]);

  const selectedEvent = useMemo(
    () => eventOptions.find((o) => o.eventId === selectedEventId) || null,
    [eventOptions, selectedEventId]
  );

  const memberList = useMemo(() => {
    if (!selectedEventId) return [];
    const enriched = enrichParticipantQrEntriesForEvent(
      familyEntries,
      allRegisteredEventRows,
      selectedEventId
    );
    const matched = (enriched || []).filter((e) =>
      participantQrEntryMatchesSearch(e, searchLower)
    );
    return sortParticipantQrEntriesByRegistered(matched);
  }, [familyEntries, allRegisteredEventRows, selectedEventId, searchLower]);

  useEffect(() => {
    setActiveUserId((prev) => {
      if (prev && memberList.some((e) => e.userId === prev)) return prev;
      if (memberList.length > 0) return memberList[0].userId;
      return "";
    });
  }, [selectedEventId, searchLower, memberList]);

  const activeEntry = useMemo(() => {
    if (!activeUserId) return null;
    return memberList.find((e) => e.userId === activeUserId) || null;
  }, [memberList, activeUserId]);

  const activeIndex = useMemo(() => {
    if (!activeUserId || memberList.length === 0) return -1;
    return memberList.findIndex((e) => e.userId === activeUserId);
  }, [memberList, activeUserId]);

  const goPrev = () => {
    if (memberList.length < 2) return;
    const idx = activeIndex >= 0 ? activeIndex : 0;
    const next = idx <= 0 ? memberList.length - 1 : idx - 1;
    setActiveUserId(memberList[next].userId);
  };

  const goNext = () => {
    if (memberList.length < 2) return;
    const idx = activeIndex >= 0 ? activeIndex : -1;
    const next = idx >= memberList.length - 1 ? 0 : idx + 1;
    setActiveUserId(memberList[next].userId);
  };

  const showCarouselChrome = memberList.length > 1 && activeIndex >= 0;

  if (!hasRegisteredEvents) {
    return (
      <div className="EventCalendar-participant-qr-panel" role="region" aria-label="Check-in QR codes">
        <div className="empty-state">
          No registered events yet. Register for an event to view check-in QR codes here.
        </div>
      </div>
    );
  }

  return (
    <div className="EventCalendar-participant-qr-panel" role="region" aria-label="Check-in QR codes">
      <div className="toolbar EventCalendar-participant-qr-panel__toolbar">
        <label className="toolbar-filter-group EventCalendar-participant-qr-panel__event-filter">
          <span className="toolbar-label">Event</span>
          <select
            className="input toolbar-control EventCalendar-participant-qr-panel__event-select"
            value={selectedEventId}
            onChange={(e) => {
              const next = String(e.target.value || "").trim();
              if (next && eventOptions.some((o) => o.eventId === next)) {
                setSelectedEventId(next);
              }
            }}
            aria-label="Select event for check-in QR codes"
          >
            {eventSelectOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {memberList.length === 0 && (
        <div className="empty-state">
          {searchLower
            ? "No participants match your search."
            : "No check-in QR codes are available for your account."}
        </div>
      )}

      {activeEntry && (
        <div className="EventCalendar-participant-qr-panel__layout">
          <div className="EventCalendar-participant-qr-panel__carousel">
            {showCarouselChrome ? (
              <button
                type="button"
                className="EventCalendar-participant-qr-panel__nav EventCalendar-participant-qr-panel__nav--prev"
                onClick={goPrev}
                aria-label="Previous participant"
              >
                <IconChevronLeft size={20} />
              </button>
            ) : (
              <span className="EventCalendar-participant-qr-panel__nav-spacer" aria-hidden="true" />
            )}
            <div className="EventCalendar-participant-qr-panel__slide">
              <ParticipantCheckInQRCard
                displayName={activeEntry.displayName}
                titledDisplayName={activeEntry.titledDisplayName}
                userId={activeEntry.userId}
                participantType={activeEntry.participantType}
                accountId={accountId}
                serverUrl={serverUrl}
                eventName={selectedEvent?.eventName || ""}
                statusKey={activeEntry.statusKey}
                statusLabel={activeEntry.statusLabel}
                size={180}
              />
              {showCarouselChrome && (
                <p className="EventCalendar-participant-qr-panel__counter" aria-live="polite">
                  {activeIndex + 1} of {memberList.length}
                </p>
              )}
            </div>
            {showCarouselChrome ? (
              <button
                type="button"
                className="EventCalendar-participant-qr-panel__nav EventCalendar-participant-qr-panel__nav--next"
                onClick={goNext}
                aria-label="Next participant"
              >
                <IconChevronRight size={20} />
              </button>
            ) : (
              <span className="EventCalendar-participant-qr-panel__nav-spacer" aria-hidden="true" />
            )}
          </div>

          <aside className="EventCalendar-participant-qr-panel__aside" aria-label="Household members">
            <ul className="EventCalendar-participant-qr-panel__member-list" role="listbox">
              {memberList.map((entry) => {
                const listStatus = formatParticipantQrListStatusLabel(entry);
                const badgeKey = entry.isRegistered ? entry.statusKey : "not-registered";
                const isSelected = entry.userId === activeUserId;
                return (
                  <li
                    key={entry.userId}
                    className={`EventCalendar-participant-qr-panel__member-row${
                      isSelected ? " is-selected" : ""
                    }`}
                  >
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`EventCalendar-participant-qr-panel__member-btn${
                        isSelected ? " is-selected" : ""
                      }`}
                      onClick={() => setActiveUserId(entry.userId)}
                    >
                      <span className="EventCalendar-participant-qr-panel__member-name">
                        {formatParticipantQrListLabel(entry)}
                      </span>
                      <span
                        className={`EventCalendar-participant-qr-panel__member-badge EventCalendar-participant-qr-panel__member-badge--${badgeKey}`}
                      >
                        {listStatus}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>
      )}
    </div>
  );
}

export default ParticipantCheckInQRPanel;
